import { Fragment } from 'react'

// Same token as spokenLabel()'s: uppercase, standalone, case-sensitive. See mapConfig.ts for why
// the preposition "at" and longer tokens containing AT must not match.
const AT_TOKEN = /(\bAT\b)/g

interface SpokenATProps {
  /** Text that may contain the "AT" abbreviation. Rendered verbatim for sighted users. */
  text: string
}

/**
 * Renders text that carries the "AT" abbreviation so it stays "AT" on screen but is announced as
 * "Assistive Technology".
 *
 * spokenLabel() covers every string that reaches a screen reader through an authored aria-label
 * or a speech-synthesis utterance. This covers the other half: label text that is announced by
 * being *read as content* (a legend row, a heading, a card title), where there is no attribute to
 * put the expansion in and where a screen reader would otherwise pronounce the bare abbreviation
 * as the English word "at".
 *
 * Text with no AT token renders as a plain string — deliberately not wrapped in an extra
 * aria-hidden/sr-only pair, which would add a second identical node to the accessibility tree for
 * no benefit.
 */
export default function SpokenAT({ text }: SpokenATProps) {
  const parts = text.split(AT_TOKEN)
  if (parts.length === 1) return <>{text}</>
  return (
    <>
      {parts.map((part, i) =>
        part === 'AT' ? (
          <Fragment key={i}>
            <span aria-hidden="true">AT</span>
            <span className="sr-only">Assistive Technology</span>
          </Fragment>
        ) : (
          <Fragment key={i}>{part}</Fragment>
        )
      )}
    </>
  )
}
